import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { GetSubscriptionsDocument, type GetSubscriptionsQuery } from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { subscriptionStateColors, translateSubscriptionState } from '@/utils/billingStateLabels';

interface SubscriptionRow {
  id: string;
  number: string;
  state: string;
  organizationUnit?: { name: string } | null;
  amountGross?: number | null;
  currencyCode?: string | null;
  intervalMonths?: number | null;
  activationDate?: string | null;
  currentTermEndDate?: string | null;
  tenantId: string;
  [key: string]: unknown;
}

export default function Subscriptions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatCurrency = (amount?: number | null, currency?: string | null) =>
    amount != null
      ? new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR' }).format(
          amount,
        )
      : '–';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const { data, loading } = useQuery<GetSubscriptionsQuery>(GetSubscriptionsDocument, {
    variables: { first: 50 },
  });

  const subscriptions = data?.subscriptions?.nodes ?? [];
  const totalCount = data?.subscriptions?.totalCount ?? 0;

  const rows: SubscriptionRow[] = subscriptions.map((s) => ({
    id: s.id,
    number: s.number,
    state: s.state,
    organizationUnit: s.organizationUnit,
    amountGross: s.amountGross,
    currencyCode: s.currencyCode,
    intervalMonths: s.intervalMonths,
    activationDate: s.activationDate,
    currentTermEndDate: s.currentTermEndDate,
    tenantId: s.tenantId,
  }));

  const columns: GridColDef[] = [
    { field: 'number', headerName: t('common.number'), width: 140 },
    {
      field: 'state',
      headerName: t('common.status'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={translateSubscriptionState(t, String(params.value))}
          size="small"
          color={subscriptionStateColors[String(params.value)] || 'default'}
        />
      ),
    },
    {
      field: 'organizationUnit',
      headerName: t('common.orgUnit'),
      flex: 1,
      valueGetter: (value: unknown) => (value as { name?: string })?.name ?? '–',
    },
    {
      field: 'amountGross',
      headerName: t('common.amount'),
      width: 110,
      valueFormatter: (value: number) => formatCurrency(value),
    },
    {
      field: 'intervalMonths',
      headerName: t('common.interval'),
      width: 90,
      valueFormatter: (value: number) => (value ? `${value} ${t('subscriptions.months')}` : '–'),
    },
    {
      field: 'activationDate',
      headerName: t('common.activated'),
      width: 110,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'currentTermEndDate',
      headerName: t('common.termUntil'),
      width: 120,
      valueFormatter: (value: string) => formatDate(value),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t('subscriptions.title')}
      </Typography>
      <DataTable<SubscriptionRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onRowClick={(row) => navigate(`/subscriptions/${row.id}`)}
        paginationMode="client"
      />
    </Box>
  );
}
